import { CaretUpDownIcon, GearIcon, SignOut, SlidersHorizontalIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { InitialsAvatar } from '~/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { useOpenSettings, useOpenSystemSettings } from '~/features/settings/use-open-settings';
import { SETTINGS_WINDOW_ID, SYSTEM_SETTINGS_WINDOW_ID } from '~/features/windows/window-titles';
import { useSignOut } from '~/hooks/mutations/use-sign-out';
import { useUserDisplayName } from '~/hooks/queries/use-tenant';
import { isVisible } from '~/lib/stores/window-rules';
import { useWindowStore } from '~/lib/stores/window-store';

/**
 * FRONT-3143: the person's corner, in the sidebar's foot. It used to be the
 * round avatar at the top right of the app header.
 *
 * FRONT-3223 (eddy, 2026-10-28): the name is one button that slides a menu up
 * — User Settings, System Settings and Sign out — instead of two icon buttons
 * beside it, and the expanded row shows the name without an avatar. A
 * settings entry still toggles its window (a shown one closes). Sign out asks
 * first, the same as in User Settings → General (`useSignOut`). The collapsed
 * rail keeps the initials as the trigger and opens the menu to the right.
 * Every sidebar state mounts this one component, never a copy.
 */
export function SidebarUserMenu({ collapsed = false }: { collapsed?: boolean }) {
  const userName = useUserDisplayName();
  const openSettings = useOpenSettings();
  const openSystemSettings = useOpenSystemSettings();
  const signOut = useSignOut();
  const queryClient = useQueryClient();
  // Shown = on screen right now; a minimized tab counts as closed, so the
  // entry brings it back rather than closing a window nobody can see.
  const userShown = useWindowStore((s) => isVisible(s, SETTINGS_WINDOW_ID));
  const systemShown = useWindowStore((s) => isVisible(s, SYSTEM_SETTINGS_WINDOW_ID));
  const userEmail = queryClient.getQueryData<{ email?: string }>(['auth', 'me'])?.email;

  return (
    <div data-testid="sidebar-user-menu" className={collapsed ? 'flex flex-col items-center' : 'flex min-w-0 flex-1'}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {collapsed ? (
            <button
              type="button"
              data-testid="sidebar-user-trigger"
              aria-label={`${userName}: settings and sign out`}
              className="flex h-9 w-9 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <InitialsAvatar name={userName} title="" size="md" tone="primary" className="h-8 w-8 rounded-full" />
            </button>
          ) : (
            <button
              type="button"
              data-testid="sidebar-user-trigger"
              aria-label={`${userName}: settings and sign out`}
              className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-left text-[13.5px] text-[var(--ink-1)] outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-accent"
            >
              <span className="min-w-0 flex-1 truncate">{userName}</span>
              <CaretUpDownIcon size={14} aria-hidden className="shrink-0 text-[var(--ink-2)]" />
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={collapsed ? 'right' : 'top'}
          align="start"
          className={collapsed ? 'w-56' : 'w-(--radix-dropdown-menu-trigger-width) min-w-56'}
        >
          <DropdownMenuLabel className="min-w-0">
            <span className="block truncate text-sm font-medium text-foreground">{userName}</span>
            {userEmail && userEmail !== userName && (
              <span className="block truncate text-xs font-normal text-muted-foreground">{userEmail}</span>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="sidebar-user-settings"
            onSelect={() => (userShown ? useWindowStore.getState().close(SETTINGS_WINDOW_ID) : openSettings())}
          >
            <GearIcon size={16} /> User Settings
          </DropdownMenuItem>
          {/* The eight former sidebar pages (FRONT-2376). Visible to every
              signed-in user; each section's own admin gate lives in the window. */}
          <DropdownMenuItem
            data-testid="sidebar-system-settings"
            onSelect={() =>
              systemShown ? useWindowStore.getState().close(SYSTEM_SETTINGS_WINDOW_ID) : openSystemSettings()
            }
          >
            <SlidersHorizontalIcon size={16} /> System Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="sidebar-sign-out"
            disabled={signOut.isPending}
            onSelect={() => void signOut.signOut()}
          >
            <SignOut size={16} /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
